import { directSupabase as supabase } from '../../lib/supabaseClient';
import {
  fromDbCashEntry,
  fromDbCashSource,
  fromDbDeduction,
  fromDbRecurrence,
  fromDbSignoff,
  monthKey,
  toDbDeduction,
  toDbRecurrence,
  type CashEntry,
  type CashSource,
  type LedgerPerson,
  type WageDeduction,
  type WageDeductionInput,
  type WageDeductionRecurrence,
  type WageDeductionRecurrenceInput,
  type WageDeductionSignoff,
} from './ledger.model';

function errorMessage(error: unknown, fallback: string): string {
  if (!error) return fallback;
  if (error instanceof Error) return error.message;
  const row = error as { message?: string; details?: string; code?: string };
  if (row.code === '42P01') return 'The ledger is not set up in the database yet. Apply 20261017140000-ledger.sql.';
  if (row.code === '23503') return 'This is still referenced by ledger entries.';
  return row.message || row.details || fallback;
}

function check<T>(result: { data: T | null; error: unknown }, fallback: string): T {
  if (result.error) throw new Error(errorMessage(result.error, fallback));
  return result.data as T;
}

export const ledgerService = {
  async listPeople(): Promise<LedgerPerson[]> {
    const rows = check(
      await supabase.from('ledger_people').select('profile_id, display_name').order('sort_order'),
      'Failed to load ledger people',
    ) as any[];
    return rows.map((row) => ({ profileId: row.profile_id, displayName: row.display_name }));
  },

  /** Whether the signed-in user is on the ledger. RLS hides the table from everyone else. */
  async canAccess(): Promise<boolean> {
    const { data, error } = await supabase.from('ledger_people').select('profile_id').limit(1);
    return !error && (data?.length ?? 0) > 0;
  },

  // Cash

  async listSources(): Promise<CashSource[]> {
    const rows = check(
      await supabase.from('cash_sources').select('*').order('sort_order').order('name'),
      'Failed to load cash holdings',
    ) as any[];
    return rows.map(fromDbCashSource);
  },

  async saveSource(source: { id?: string; name: string; description: string | null; isArchived?: boolean }): Promise<CashSource> {
    const patch = {
      name: source.name.trim(),
      description: source.description?.trim() || null,
      is_archived: source.isArchived ?? false,
    };
    const query = source.id
      ? supabase.from('cash_sources').update(patch).eq('id', source.id)
      : supabase.from('cash_sources').insert(patch);
    return fromDbCashSource(check(await query.select('*').single(), 'Failed to save cash holding'));
  },

  async listEntries(): Promise<CashEntry[]> {
    const rows = check(
      await supabase.from('cash_entries').select('*').order('entry_date').order('created_at'),
      'Failed to load cash entries',
    ) as any[];
    return rows.map(fromDbCashEntry);
  },

  async saveEntry(entry: { id?: string; sourceId: string; entryDate: string; description: string; amount: number }): Promise<CashEntry> {
    const patch = {
      source_id: entry.sourceId,
      entry_date: entry.entryDate,
      description: entry.description.trim(),
      amount: entry.amount,
    };
    const query = entry.id
      ? supabase.from('cash_entries').update(patch).eq('id', entry.id)
      : supabase.from('cash_entries').insert(patch);
    return fromDbCashEntry(check(await query.select('*').single(), 'Failed to save cash entry'));
  },

  /** Moves cash between holdings as a pair of entries sharing a transfer id. */
  async transfer(args: { fromId: string; toId: string; entryDate: string; amount: number; description: string }): Promise<CashEntry[]> {
    const transferId = crypto.randomUUID();
    const description = args.description.trim() || 'Transfer';
    const rows = check(
      await supabase.from('cash_entries').insert([
        { source_id: args.fromId, entry_date: args.entryDate, description, amount: -args.amount, transfer_id: transferId },
        { source_id: args.toId, entry_date: args.entryDate, description, amount: args.amount, transfer_id: transferId },
      ]).select('*'),
      'Failed to record transfer',
    ) as any[];
    return rows.map(fromDbCashEntry);
  },

  /** Deleting one side of a transfer deletes both. */
  async deleteEntry(entry: CashEntry): Promise<void> {
    const query = entry.transferId
      ? supabase.from('cash_entries').delete().eq('transfer_id', entry.transferId)
      : supabase.from('cash_entries').delete().eq('id', entry.id);
    check(await query, 'Failed to delete cash entry');
  },

  // Wage deductions

  /** Creates any recurring rows due up to the month first, so the month is complete. */
  async listDeductions(month: string): Promise<WageDeduction[]> {
    check(
      await supabase.rpc('generate_recurring_wage_deductions', { p_through: monthKey(month) }),
      'Failed to create recurring deductions',
    );
    const rows = check(
      await supabase.from('wage_deductions').select('*')
        .eq('deduction_month', monthKey(month))
        .order('purchased_on').order('created_at'),
      'Failed to load wage deductions',
    ) as any[];
    return rows.map(fromDbDeduction);
  },

  async saveDeduction(input: WageDeductionInput, id?: string): Promise<WageDeduction> {
    const patch = toDbDeduction(input);
    const query = id
      ? supabase.from('wage_deductions').update(patch).eq('id', id)
      : supabase.from('wage_deductions').insert(patch);
    return fromDbDeduction(check(await query.select('*').single(), 'Failed to save deduction'));
  },

  async deleteDeduction(id: string): Promise<void> {
    check(await supabase.from('wage_deductions').delete().eq('id', id), 'Failed to delete deduction');
  },

  async listRecurrences(): Promise<WageDeductionRecurrence[]> {
    const rows = check(
      await supabase.from('wage_deduction_recurrences').select('*').order('start_month').order('created_at'),
      'Failed to load recurring deductions',
    ) as any[];
    return rows.map(fromDbRecurrence);
  },

  async createRecurrence(input: WageDeductionRecurrenceInput): Promise<WageDeductionRecurrence> {
    return fromDbRecurrence(check(
      await supabase.from('wage_deduction_recurrences').insert(toDbRecurrence(input)).select('*').single(),
      'Failed to save recurring deduction',
    ));
  },

  /** Saves the new schedule and rebuilds its rows from `fromMonth` on. Affirmed months are kept. */
  async updateRecurrence(id: string, input: WageDeductionRecurrenceInput, fromMonth: string): Promise<WageDeductionRecurrence> {
    const saved = fromDbRecurrence(check(
      await supabase.from('wage_deduction_recurrences').update(toDbRecurrence(input)).eq('id', id).select('*').single(),
      'Failed to save recurring deduction',
    ));
    check(
      await supabase.rpc('reset_wage_deduction_recurrence', { p_recurrence_id: id, p_from_month: monthKey(fromMonth) }),
      'Failed to update the recurring rows',
    );
    return saved;
  },

  /** Removes the schedule and its rows from `fromMonth` on. Earlier rows stay as one-off deductions. */
  async deleteRecurrence(id: string, fromMonth: string): Promise<void> {
    check(
      await supabase.rpc('delete_wage_deduction_recurrence', { p_recurrence_id: id, p_from_month: monthKey(fromMonth) }),
      'Failed to delete recurring deduction',
    );
  },

  async listSignoffs(fromMonth: string, toMonth: string): Promise<WageDeductionSignoff[]> {
    const rows = check(
      await supabase.from('wage_deduction_signoffs').select('*')
        .gte('deduction_month', monthKey(fromMonth))
        .lte('deduction_month', monthKey(toMonth)),
      'Failed to load sign-offs',
    ) as any[];
    return rows.map(fromDbSignoff);
  },

  async affirmMonth(profileId: string, month: string): Promise<WageDeductionSignoff> {
    const row = check(
      await supabase.rpc('affirm_wage_deduction_month', { p_profile_id: profileId, p_month: monthKey(month) }),
      'Failed to affirm deductions',
    );
    return fromDbSignoff(row as any);
  },

  async withdrawMonth(profileId: string, month: string): Promise<void> {
    check(
      await supabase.rpc('withdraw_wage_deduction_month', { p_profile_id: profileId, p_month: monthKey(month) }),
      'Failed to withdraw affirmation',
    );
  },
};
